import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Header';
import { useAuth } from '../../auth/AuthProvider';

import './NxlStats.css';

import {
  PRIVATE_MARKET_API,
  PUBLIC_MARKET_API,
  authHeaders,
  fetchJson,
} from './nxlApi';

import {
  calculatePoolEstimate,
  emptyPoolStats,
  isScheduledMarket,
} from './nxlUtils';

import NxlDashboardHeader from './NxlDashboardHeader';
import NxlMobileNav from './NxlMobileNav';
import MarketRules from './MarketRules';
import MarketBoard from './MarketBoard';
import HotMarkets from './HotMarkets';
import TradePanel from './TradePanel';
import PositionsPanel from './PositionsPanel';
import LeaderboardPanel from './LeaderboardPanel';

function NxlStats() {
  const { token, isAuthenticated, loginWithRedirect, isLoading } = useAuth();

  const [activeSection, setActiveSection] = useState('markets');
  const [stats, setStats] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  const [selectedDivision, setSelectedDivision] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);

  const [marketPoolStats, setMarketPoolStats] = useState(null);
  const [poolStatsByMarket, setPoolStatsByMarket] = useState({});

  const [stake, setStake] = useState(100);
  const [side, setSide] = useState('team_a');
  const [quote, setQuote] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);

  const [positions, setPositions] = useState([]);
  const [positionTab, setPositionTab] = useState('open');

  const [loading, setLoading] = useState(true);
  const [marketStatsLoading, setMarketStatsLoading] = useState(false);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tradeMessage, setTradeMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const poolEstimate = useMemo(() => {
    return calculatePoolEstimate(marketPoolStats, side, stake);
  }, [marketPoolStats, side, stake]);

  const hotMarkets = useMemo(() => {
    return markets
      .map((market) => ({
        market,
        pool: poolStatsByMarket[market._id] || emptyPoolStats(),
      }))
      .sort((a, b) => {
        const creditsDiff =
          Number(b.pool?.credits_wagered || 0) - Number(a.pool?.credits_wagered || 0);

        if (creditsDiff !== 0) return creditsDiff;

        return Number(b.pool?.positions || 0) - Number(a.pool?.positions || 0);
      })
      .slice(0, 12);
  }, [markets, poolStatsByMarket]);

  async function loadPublicData() {
    const [statsData, divisionsData, leaderboardData] = await Promise.all([
      fetchJson(`${PUBLIC_MARKET_API}/stats`),
      fetchJson(`${PUBLIC_MARKET_API}/divisions`),
      fetchJson(`${PUBLIC_MARKET_API}/leaderboard?limit=10`),
    ]);

    setStats(statsData);
    setDivisions(divisionsData.divisions || []);
    setLeaderboard(leaderboardData.leaderboard || []);

    const firstDivision =
      divisionsData?.divisions?.find((d) => d.division === 'Pro X-Ball')?.division ||
      divisionsData?.divisions?.[0]?.division ||
      '';

    setSelectedDivision((prev) => prev || firstDivision);
  }

  async function loadPrivateDashboard() {
    if (!isAuthenticated || !token) return;

    const data = await fetchJson(`${PRIVATE_MARKET_API}/dashboard`, {
      headers: authHeaders(token),
    });

    setDashboard(data);
  }

  async function loadMarkets(division, team = '') {
    if (!division) return;

    const params = new URLSearchParams({
      status: 'open',
      division,
      limit: '200',
      page: '1',
    });

    if (team.trim()) params.set('team', team.trim());

    const data = await fetchJson(`${PUBLIC_MARKET_API}/markets?${params.toString()}`);
    const scheduledMarkets = (data.markets || []).filter(isScheduledMarket);

    setMarkets(scheduledMarkets);

    if (scheduledMarkets.length) {
      setSelectedMarket((prev) => {
        if (prev && scheduledMarkets.some((m) => m._id === prev._id)) return prev;
        return scheduledMarkets[0];
      });
    } else {
      setSelectedMarket(null);
    }

    return scheduledMarkets;
  }

  async function loadMarketPoolStats(marketId) {
    if (!marketId) {
      setMarketPoolStats(null);
      return null;
    }

    try {
      setMarketStatsLoading(true);

      const data = await fetchJson(`${PUBLIC_MARKET_API}/markets/${marketId}/stats`);
      const safeData = data || emptyPoolStats();

      setMarketPoolStats(safeData);
      setPoolStatsByMarket((prev) => ({
        ...prev,
        [marketId]: safeData,
      }));

      return safeData;
    } catch (err) {
      console.warn('[NXL market pool stats failed]', err);

      setMarketPoolStats(emptyPoolStats());
      return emptyPoolStats();
    } finally {
      setMarketStatsLoading(false);
    }
  }

  async function warmHotMarketStats(marketList) {
    const top = (marketList || []).slice(0, 24);

    await Promise.allSettled(
      top.map(async (market) => {
        const data = await fetchJson(`${PUBLIC_MARKET_API}/markets/${market._id}/stats`);
        setPoolStatsByMarket((prev) => ({
          ...prev,
          [market._id]: data || emptyPoolStats(),
        }));
      })
    );
  }

  async function loadPositions() {
    if (!isAuthenticated || !token) return;

    try {
      setPositionsLoading(true);

      const data = await fetchJson(`${PRIVATE_MARKET_API}/positions`, {
        headers: authHeaders(token),
      });

      setPositions(data.positions || []);
    } finally {
      setPositionsLoading(false);
    }
  }

  async function refreshAll() {
    try {
      setError('');
      await loadPublicData();

      const currentMarkets = await loadMarkets(selectedDivision, teamFilter);

      if (selectedMarket?._id) {
        await loadMarketPoolStats(selectedMarket._id);
      }

      if (currentMarkets?.length) {
        await warmHotMarketStats(currentMarkets);
      }

      if (isAuthenticated && token) {
        await Promise.all([
          loadPrivateDashboard(),
          loadPositions(),
        ]);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('[NXL refresh failed]', err);
      setError(err.message || 'Failed to refresh dashboard.');
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        setLoading(true);
        setError('');

        await loadPublicData();

        if (!cancelled && isAuthenticated && token) {
          await Promise.all([
            loadPrivateDashboard(),
            loadPositions(),
          ]);
        }

        if (!cancelled) setLastUpdated(new Date());
      } catch (err) {
        console.error('[NXL prediction market load failed]', err);
        if (!cancelled) setError(err.message || 'Prediction market failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!selectedDivision) return;

    const timer = setTimeout(() => {
      loadMarkets(selectedDivision, teamFilter)
        .then((loaded) => warmHotMarketStats(loaded))
        .catch((err) => {
          console.error('[NXL market list failed]', err);
          setError(err.message || 'Unable to load scheduled markets.');
        });
    }, 250);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDivision, teamFilter]);

  useEffect(() => {
    setQuote(null);
    setTradeMessage('');
    setConfirmStep(false);

    if (selectedMarket?._id) {
      loadMarketPoolStats(selectedMarket._id);
    } else {
      setMarketPoolStats(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket?._id, side, stake]);

  const clearFilters = () => {
    setTeamFilter('');
    setSelectedMarket(null);
  };

  const getQuote = async () => {
    if (!selectedMarket || !isAuthenticated || !token || tradeLoading) return;

    if (!isScheduledMarket(selectedMarket)) {
      setError('Only scheduled PBLeagues matches are tradable.');
      return;
    }

    try {
      setTradeLoading(true);
      setError('');
      setTradeMessage('');
      setConfirmStep(false);

      const data = await fetchJson(`${PRIVATE_MARKET_API}/quote`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          marketId: selectedMarket._id,
          side,
          stake: Number(stake),
        }),
      });

      setQuote(data.quote);
      await loadMarketPoolStats(selectedMarket._id);
    } catch (err) {
      console.error('[NXL quote failed]', err);
      setQuote(null);
      setError(err.message || 'Quote failed.');
    } finally {
      setTradeLoading(false);
    }
  };

  const placePosition = async () => {
    if (!selectedMarket || !isAuthenticated || !token || !quote || tradeLoading) return;

    try {
      setTradeLoading(true);
      setError('');
      setTradeMessage('');

      const idempotencyKey = `${selectedMarket._id}:${side}:${stake}:${Date.now()}`;

      await fetchJson(`${PRIVATE_MARKET_API}/positions`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          marketId: selectedMarket._id,
          side,
          stake: Number(stake),
          idempotencyKey,
        }),
      });

      setTradeMessage('Position placed.');
      setQuote(null);
      setConfirmStep(false);

      await Promise.all([
        loadPrivateDashboard(),
        loadPositions(),
        loadMarketPoolStats(selectedMarket._id),
      ]);

      setActiveSection('positions');
    } catch (err) {
      console.error('[NXL position failed]', err);
      setError(err.message || 'Position failed.');
    } finally {
      setTradeLoading(false);
    }
  };

  const showSection = (section) => activeSection === section;

  if (isLoading || loading) {
    return (
      <div className="nxl-page">
        <Header />
        <main className="nxl-shell">
          <div className="nxl-panel">Loading live market dashboard…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="nxl-page">
      <Header />

      <main className="nxl-shell">
        <NxlDashboardHeader
          stats={stats}
          markets={markets}
          dashboard={dashboard}
          lastUpdated={lastUpdated}
          refreshAll={refreshAll}
          loading={loading}
        />

        {error && <div className="nxl-error">{error}</div>}
        {tradeMessage && <div className="nxl-success">{tradeMessage}</div>}

        <NxlMobileNav
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />

        <div className="nxl-dashboard-grid">
          <section className="nxl-dashboard-main">
            <div className={showSection('markets') ? 'nxl-section-active' : 'nxl-section-mobile-hidden'}>
              <MarketBoard
                divisions={divisions}
                selectedDivision={selectedDivision}
                setSelectedDivision={setSelectedDivision}
                teamFilter={teamFilter}
                setTeamFilter={setTeamFilter}
                markets={markets}
                selectedMarket={selectedMarket}
                setSelectedMarket={(market) => {
                  setSelectedMarket(market);
                  setActiveSection('ticket');
                }}
                clearFilters={clearFilters}
              />
            </div>

            <div className={showSection('hot') ? 'nxl-section-active' : 'nxl-section-mobile-hidden'}>
              <HotMarkets
                hotMarkets={hotMarkets}
                selectedMarket={selectedMarket}
                setSelectedMarket={(market) => {
                  setSelectedMarket(market);
                  setActiveSection('ticket');
                }}
              />
            </div>

            <div className={showSection('positions') ? 'nxl-section-active' : 'nxl-section-mobile-hidden'}>
              <PositionsPanel
                dashboard={dashboard}
                positions={positions}
                positionTab={positionTab}
                setPositionTab={setPositionTab}
                positionsLoading={positionsLoading}
                loadPositions={loadPositions}
              />
            </div>

            <div className={showSection('rules') ? 'nxl-section-active' : 'nxl-section-mobile-hidden'}>
              <MarketRules />
              <LeaderboardPanel leaderboard={leaderboard} />
            </div>
          </section>

          <aside className={showSection('ticket') ? 'nxl-dashboard-side nxl-section-active' : 'nxl-dashboard-side nxl-section-mobile-hidden'}>
            <TradePanel
              selectedMarket={selectedMarket}
              side={side}
              setSide={setSide}
              stake={stake}
              setStake={setStake}
              marketPoolStats={marketPoolStats}
              marketStatsLoading={marketStatsLoading}
              isAuthenticated={isAuthenticated}
              loginWithRedirect={loginWithRedirect}
              quote={quote}
              getQuote={getQuote}
              tradeLoading={tradeLoading}
              confirmStep={confirmStep}
              setConfirmStep={setConfirmStep}
              poolEstimate={poolEstimate}
              placePosition={placePosition}
            />

            <LeaderboardPanel leaderboard={leaderboard} />
          </aside>
        </div>

        <section className="nxl-footer-disclaimer">
          Prediction markets currently use simulated credits. Model odds are generated from NXL
          matchup forecasting models. Final payouts are based on pool participation and may differ
          from model-implied returns. Only scheduled PBLeagues matches are eligible for settlement.
        </section>
      </main>
    </div>
  );
}

export default NxlStats;